import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Offline PWA rule: writes are BLOCKED while the device is offline (never queued or auto-replayed).
// The user retries explicitly once the OFFLINE banner clears. Reads go through the SW cache rules.
const WRITE_METHODS = new Set(["post", "put", "patch", "delete"]);
export const isOfflineWriteBlocked = (err) => err?.code === "F2N_OFFLINE_WRITE_BLOCKED";

api.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (WRITE_METHODS.has(method) && typeof navigator !== "undefined" && navigator.onLine === false) {
    const err = new Error("You are offline. Nothing was sent — retry when you are back online.");
    err.code = "F2N_OFFLINE_WRITE_BLOCKED";
    err.config = cfg;
    try { window.dispatchEvent(new CustomEvent("f2n:offline-write-blocked", { detail: { url: cfg.url, method } })); }
    catch { /* SSR-safe */ }
    return Promise.reject(err);
  }
  return cfg;
});

// SEC-STEP-01: critical actions answer 403 step_up_required; ask for a fresh 2FA code, then retry once.
let stepUpHandler = null;
export const setStepUpHandler = (fn) => { stepUpHandler = fn; };
// The admin grant is reusable server-side for 5 min within this session; re-send it on writes.
let adminGrant = null;

api.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (adminGrant && adminGrant.until > Date.now() && WRITE_METHODS.has(method) && !cfg.headers?.["X-Step-Up-Token"]) {
    cfg.headers = { ...cfg.headers, "X-Step-Up-Token": adminGrant.token };
  }
  return cfg;
});

const stepUpRejected = (err, message) => {
  // Callers often render detail directly (toast.error(detail)); never hand them the challenge object.
  err.response.data = { ...err.response.data, detail: message };
  return Promise.reject(err);
};

api.interceptors.response.use(undefined, async (err) => {
  const detail = err?.response?.data?.detail;
  const cfg = err?.config;
  if (err?.response?.status === 403 && detail?.code === "admin_2fa_enrollment_required") {
    return stepUpRejected(err, "Admin access requires two-factor authentication. Turn it on in Security → Two-factor, then try again.");
  }
  if (err?.response?.status !== 403 || detail?.code !== "step_up_required") return Promise.reject(err);
  if (!cfg || cfg._stepUpRetried || !stepUpHandler) {
    return stepUpRejected(err, "Confirmation with your authenticator code is required. Please try again.");
  }
  const token = await stepUpHandler(detail);
  if (!token) return stepUpRejected(err, "Cancelled — nothing was changed.");
  if (detail.scope === "admin") adminGrant = { token, until: Date.now() + 280 * 1000 };
  return api({ ...cfg, _stepUpRetried: true, headers: { ...cfg.headers, "X-Step-Up-Token": token } });
});

export const CATEGORIES = [
  // ---- Basic-needs sequence (as requested) ----
  { key: "job", label: "Jobs & Vacancies", icon: "Briefcase", desc: "MNC + local vacancies" },
  { key: "dating", label: "Dating (18+)", icon: "HeartHandshake", desc: "KYC-verified profiles" },
  { key: "matrimonial", label: "Matrimonial", icon: "Heart", desc: "Find your match" },
  { key: "restaurant", label: "Food", icon: "UtensilsCrossed", desc: "Restaurants, cafés, delivery" },
  { key: "property", label: "Shelter", icon: "House", desc: "Rent, PG, flats, homestays" },
  { key: "product", label: "Clothes & Shopping", icon: "ShoppingBag", desc: "Fashion, brands, essentials" },
  { key: "school", label: "Kids", icon: "GraduationCap", desc: "Schools, tutors, day-care, baby care" },
  { key: "doctor", label: "Doctors", icon: "Stethoscope", desc: "Consult a doctor nearby" },
  { key: "service", label: "Services", icon: "Wrench", desc: "Plumber, teacher, maid, electrician" },
  { key: "vacation", label: "Vacation", icon: "Palmtree", desc: "Beaches, hills, tours, packages" },

  // ---- Everything else (retained for discovery) ----
  { key: "hospital", label: "Hospitals", icon: "FirstAid", desc: "24x7 emergency care" },
  { key: "baby_care", label: "Baby Care", icon: "Baby", desc: "Baby products & care" },
  { key: "daycare", label: "Daycare", icon: "Blocks", desc: "Creche & preschool" },
  { key: "tutor", label: "Tutors", icon: "PencilRuler", desc: "Home & online tuition" },
  { key: "hotel", label: "Hotels", icon: "BedDouble", desc: "Rooms & stays" },
  { key: "rental", label: "Rentals", icon: "Bike", desc: "Bike, camera, gear" },
  { key: "store", label: "Stores", icon: "Storefront", desc: "Nearby shops" },
  { key: "brand", label: "Brands", icon: "Star", desc: "Verified brands" },
  { key: "salon", label: "Salon & Spa", icon: "Scissors", desc: "Hair, beauty, spa" },
  { key: "gym", label: "Gym & Fitness", icon: "Dumbbell", desc: "Gyms, yoga, pilates" },
  { key: "pet_care", label: "Pet Care", icon: "Dog", desc: "Grooming, walking, sitting" },
  { key: "veterinarian", label: "Veterinarians", icon: "Stethoscope", desc: "Vets & pet clinics" },
  { key: "photographer", label: "Photographers", icon: "Camera", desc: "Wedding, portrait, event" },
  { key: "wedding_planner", label: "Wedding Planners", icon: "HeartHandshake", desc: "End-to-end planning" },
  { key: "elder_care", label: "Elder Care", icon: "HeartPulse", desc: "Home nursing & assistance" },
  { key: "physiotherapy", label: "Physiotherapy", icon: "Activity", desc: "Recovery & rehab" },
  { key: "loan", label: "Loans & Finance", icon: "Landmark", desc: "Loans against gold, property, salary" },
  { key: "investment", label: "Investments", icon: "LineChart", desc: "FD, RD, Equity, Gold, Insurance, Tax-Saving" },
  { key: "cab", label: "Cab / Ride", icon: "Car", desc: "Book a ride nearby" },
  { key: "flight", label: "Flights", icon: "Plane", desc: "Domestic & international", secondary: true },
  { key: "train", label: "Trains", icon: "TrainFront", desc: "Rail tickets & PNR", secondary: true },
  { key: "bus", label: "Buses", icon: "Bus", desc: "Inter-city & sleeper", secondary: true },
  { key: "marriage_hall", label: "Marriage Halls", icon: "Church", desc: "Wedding venues", secondary: true },
  { key: "party_hall", label: "Party Halls", icon: "PartyPopper", desc: "Birthdays, corporate", secondary: true },
  { key: "movie", label: "Movies", icon: "Film", desc: "Cinema tickets, screenings, film events", secondary: true },
  { key: "music", label: "Music", icon: "Music", desc: "Live gigs, concerts, artists, records", secondary: true },
  { key: "yojna", label: "Govt Yojna", icon: "Buildings", desc: "Schemes & benefits", secondary: true },
  { key: "commodity", label: "Gold & Metals", icon: "Coin", desc: "Live market prices", secondary: true },
  { key: "antique", label: "Antiques", icon: "Trophy", desc: "Auctions & buy-now", secondary: true },
  { key: "developer", label: "Developers", icon: "Code", desc: "App, web & software freelancers", secondary: true },
];

export const CAT_MAP = Object.fromEntries(CATEGORIES.map(c => [c.key, c]));

// Category presets: sensible defaults + subtype options that auto-populate on category change in PostListing.
// Fields defined here override current form values when user switches categories.
export const CATEGORY_PRESETS = {
  service: {
    price_unit: "hour",
    title_placeholder: "e.g., Ramesh · AC Repair Expert",
    desc_placeholder: "Years of experience, what services you offer, service radius…",
    tags_placeholder: "plumber, leak repair, tap fitting",
    subtype_label: "Type of service",
    subtypes: ["plumber", "electrician", "cleaner", "carpenter", "painter", "AC repair", "appliance repair", "tutor", "beautician", "photographer", "caretaker (elders)", "caretaker (children)", "caretaker (patient)", "other"],
  },
  product: {
    price_unit: "fixed",
    title_placeholder: "e.g., Handmade Kolhapuri Chappals",
    desc_placeholder: "What are you selling? Materials, size, colours…",
    tags_placeholder: "handmade, leather, footwear",
    subtype_label: "Product type",
    subtypes: ["clothing", "footwear", "electronics", "home decor", "beauty", "handicraft", "food", "other"],
  },
  rental: {
    price_unit: "day",
    title_placeholder: "e.g., Royal Enfield 350 on Rent",
    desc_placeholder: "What is on rent? Deposit, terms, availability…",
    tags_placeholder: "bike, weekend, self-drive",
    subtype_label: "Rental type",
    subtypes: ["bike", "car", "camera", "gear", "tools", "furniture", "party equipment", "other"],
  },
  property: {
    price_unit: "month",
    title_placeholder: "e.g., 2BHK · Andheri West · Semi-furnished",
    desc_placeholder: "BHK, floor, amenities, deposit, availability…",
    tags_placeholder: "2bhk, semi-furnished, pet-friendly",
    subtype_label: "Property type",
    subtypes: ["1RK", "1BHK", "2BHK", "3BHK", "4BHK+", "PG / Hostel", "Commercial", "Plot / Land"],
  },
  hospital: {
    price_unit: "fixed",
    title_placeholder: "e.g., Sunrise Multi-Speciality Hospital",
    desc_placeholder: "Specialities, ICU, ambulance, 24x7 emergency…",
    tags_placeholder: "24x7, ICU, cardiology",
    subtype_label: "Facility type",
    subtypes: ["Multi-speciality", "Clinic", "Diagnostic centre", "Dental", "Eye care", "Maternity", "Ayurveda"],
  },
  doctor: {
    price_unit: "per_visit",
    title_placeholder: "e.g., Dr. Sharma · Cardiologist",
    desc_placeholder: "Speciality, MBBS/MD, years, timings, hospital affiliations…",
    tags_placeholder: "cardiologist, MD, evening slots",
    subtype_label: "Speciality",
    subtypes: ["General Physician", "Cardiologist", "Dentist", "Dermatologist", "Gynaecologist", "Paediatrician", "Orthopaedic", "ENT", "Psychiatrist", "Ayurvedic", "Homoeopath"],
  },
  job: {
    price_unit: "month",
    title_placeholder: "e.g., Senior Backend Engineer @ Infosys",
    desc_placeholder: "Role, responsibilities, must-have skills, salary range…",
    tags_placeholder: "python, fastapi, remote",
    subtype_label: "Employment type",
    subtypes: ["Full-time", "Part-time", "Contract", "Internship", "Freelance", "Remote"],
  },
  matrimonial: {
    price_unit: "fixed",
    title_placeholder: "e.g., Priya, 27, Software Engineer",
    desc_placeholder: "Age, height, education, profession, family, expectations…",
    tags_placeholder: "MBA, family-oriented, Mumbai",
    subtype_label: "Looking as",
    subtypes: ["Bride", "Groom"],
    subtype_required: true,
  },
  store: {
    price_unit: "fixed",
    title_placeholder: "e.g., Sharma General Store",
    desc_placeholder: "What does your store sell? Opening hours, delivery…",
    tags_placeholder: "kirana, groceries, home delivery",
    subtype_label: "Store type",
    subtypes: ["Kirana", "Bakery", "Pharmacy", "Stationery", "Hardware", "Boutique", "Salon", "Restaurant", "Other"],
  },
  brand: {
    price_unit: "fixed",
    title_placeholder: "e.g., Kaya Skin Clinic",
    desc_placeholder: "Brand story, products, outlets, USP…",
    tags_placeholder: "skincare, trusted, verified",
    subtype_label: "Brand type",
    subtypes: ["Local", "Regional", "National", "International"],
  },
  yojna: {
    price_unit: "fixed",
    title_placeholder: "e.g., PM Kisan Samman Nidhi",
    desc_placeholder: "Scheme benefits, eligibility, how to apply, documents…",
    tags_placeholder: "farmer, subsidy, central",
    subtype_label: "Level",
    subtypes: ["Central", "State", "District", "Municipal"],
  },
  commodity: {
    price_unit: "gram",
    title_placeholder: "e.g., Zaveri Bazaar Bullion Co.",
    desc_placeholder: "Metals dealt, purity, live rates, physical delivery…",
    tags_placeholder: "gold, silver, 24k",
    subtype_label: "Metals dealt",
    subtypes: ["Gold", "Silver", "Platinum", "Copper", "Multi-metal"],
  },
  antique: {
    price_unit: "fixed",
    title_placeholder: "e.g., Vintage Tanjore Painting (1920s)",
    desc_placeholder: "Origin, age, condition, provenance…",
    tags_placeholder: "vintage, art, collectible",
    subtype_label: "Antique type",
    subtypes: ["Coin / Currency", "Painting", "Jewellery", "Furniture", "Statue / Idol", "Manuscript", "Other"],
    default_sale_type: "auction",
  },
  cab: {
    price_unit: "km",
    title_placeholder: "e.g., Airport Drop · Sedan",
    desc_placeholder: "Vehicle type, seats, AC, luggage, fare per km…",
    tags_placeholder: "sedan, airport, outstation",
    subtype_label: "Ride type",
    subtypes: ["Local", "Outstation", "Airport transfer", "Rental (hourly)", "Sharing"],
  },
  developer: {
    price_unit: "hour",
    title_placeholder: "e.g., Karthik · Full-stack Developer",
    desc_placeholder: "Stack, portfolio, past projects, availability…",
    tags_placeholder: "react, fastapi, aws",
    subtype_label: "Specialisation",
    subtypes: ["Frontend", "Backend", "Full-stack", "Mobile", "DevOps", "Data / ML", "Web3 / Blockchain"],
  },
  restaurant: {
    price_unit: "per_person",
    title_placeholder: "e.g., Copper Chimney · North Indian",
    desc_placeholder: "Cuisine, seating, timings, avg cost, must-try dishes…",
    tags_placeholder: "north-indian, family, ac, parking",
    subtype_label: "Cuisine / vibe",
    subtypes: ["Fine Dining", "Casual Dining", "Café", "Fast Food", "Cloud Kitchen", "Bar / Pub", "Bakery"],
  },
  hotel: {
    price_unit: "night",
    title_placeholder: "e.g., The Leela · Bandra Kurla",
    desc_placeholder: "Room type, amenities, check-in / out, distance from station…",
    tags_placeholder: "wifi, breakfast, pool",
    subtype_label: "Stay type",
    subtypes: ["Budget", "3-star", "4-star", "5-star", "Boutique", "Resort", "Homestay / BnB", "Service Apartment"],
  },
  flight: {
    price_unit: "fixed",
    title_placeholder: "e.g., DEL → BOM · IndiGo 6E-2135",
    desc_placeholder: "Sector, timings, baggage, class, layovers…",
    tags_placeholder: "domestic, morning, non-stop",
    subtype_label: "Route type",
    subtypes: ["Domestic Non-stop", "Domestic Layover", "International Non-stop", "International Layover", "Charter"],
    subtype_required: true,
  },
  train: {
    price_unit: "fixed",
    title_placeholder: "e.g., 12951 · Mumbai Rajdhani",
    desc_placeholder: "Train number, class, quota, boarding station, arrival…",
    tags_placeholder: "rajdhani, superfast, weekly",
    subtype_label: "Class",
    subtypes: ["AC First (1A)", "AC 2-Tier (2A)", "AC 3-Tier (3A)", "AC Chair (CC)", "Sleeper (SL)", "General (GN)", "Tatkal"],
    subtype_required: true,
  },
  bus: {
    price_unit: "fixed",
    title_placeholder: "e.g., Mumbai → Pune · Neeta Volvo",
    desc_placeholder: "Boarding point, seat type, drop location, timings…",
    tags_placeholder: "volvo, sleeper, overnight",
    subtype_label: "Bus type",
    subtypes: ["Volvo AC Sleeper", "Volvo AC Semi-Sleeper", "Non-AC Sleeper", "Non-AC Seater", "Local", "State Transport"],
    subtype_required: true,
  },
  marriage_hall: {
    price_unit: "day",
    title_placeholder: "e.g., Grand Sarovar · Banquet Hall",
    desc_placeholder: "Seating capacity, catering, décor, parking, in-house menu…",
    tags_placeholder: "wedding, banquet, valet",
    subtype_label: "Venue style",
    subtypes: ["Banquet Hall", "Lawn / Garden", "Farmhouse", "Convention Centre", "Destination", "Beach / Resort"],
  },
  party_hall: {
    price_unit: "day",
    title_placeholder: "e.g., SkyDeck Rooftop · Bandra",
    desc_placeholder: "Capacity, DJ, catering, décor, minimum spend, dress code…",
    tags_placeholder: "birthday, corporate, rooftop",
    subtype_label: "Party type",
    subtypes: ["Kids Birthday", "Corporate", "Anniversary", "Bachelor / Bachelorette", "Cocktail Rooftop", "Private Dining"],
  },
  movie: {
    price_unit: "per_person",
    title_placeholder: "e.g., Dune Part Three · IMAX 3D",
    desc_placeholder: "Movie, screen, timings, seats, snacks combo…",
    tags_placeholder: "imax, 3d, weekend",
    subtype_label: "Screen type",
    subtypes: ["Multiplex", "Single Screen", "IMAX", "4DX", "Drive-in", "Private Screening", "Recliner / Gold"],
  },
  baby_care: {
    price_unit: "fixed",
    title_placeholder: "e.g., Tiny Feet Store · Diapers, formula, strollers",
    desc_placeholder: "Age range, product line, delivery, brand certifications…",
    tags_placeholder: "diapers, formula, strollers, organic",
    subtype_label: "Category",
    subtypes: ["Diapers & Wipes", "Feeding & Formula", "Strollers & Car Seats", "Toys 0-3", "Baby Clothing", "Baby Food", "Skincare"],
  },
  daycare: {
    price_unit: "month",
    title_placeholder: "e.g., Little Elly · Full-day Creche",
    desc_placeholder: "Age range, timings, meals, staff qualifications, safety…",
    tags_placeholder: "creche, preschool, meals, cctv",
    subtype_label: "Age group",
    subtypes: ["Infant (0-12mo)", "Toddler (1-3yr)", "Pre-nursery (3-4yr)", "Full-day", "Half-day", "Weekend"],
  },
  school: {
    price_unit: "month",
    title_placeholder: "e.g., DPS · CBSE · Grades I-XII",
    desc_placeholder: "Board, medium, fees, facilities, admission cycle…",
    tags_placeholder: "cbse, coed, transport",
    subtype_label: "Board / type",
    subtypes: ["CBSE", "ICSE", "State Board", "IB", "IGCSE", "Boarding", "Montessori", "Special Needs"],
    subtype_required: true,
  },
  tutor: {
    price_unit: "hour",
    title_placeholder: "e.g., Priya · Maths tuition · Class VIII-X",
    desc_placeholder: "Subjects, grade levels, mode (home/online), fees, batch size…",
    tags_placeholder: "maths, JEE, home tuition",
    subtype_label: "Mode",
    subtypes: ["Home visit", "Student's home", "Online 1-on-1", "Group class", "Weekend batch"],
  },
  gym: {
    price_unit: "month",
    title_placeholder: "e.g., Cult.fit · Bandra West · Group workouts",
    desc_placeholder: "Facilities, trainers, timings, personal training options…",
    tags_placeholder: "gym, crossfit, yoga, personal trainer",
    subtype_label: "Type",
    subtypes: ["Gym / Weight Training", "CrossFit", "Yoga Studio", "Pilates", "Zumba / Dance", "Martial Arts", "Swimming"],
  },
  salon: {
    price_unit: "per_visit",
    title_placeholder: "e.g., Green Trends · Unisex Salon",
    desc_placeholder: "Services, brands used, timings, home visits…",
    tags_placeholder: "haircut, keratin, bridal, unisex",
    subtype_label: "Service focus",
    subtypes: ["Unisex Salon", "Men's Salon / Barber", "Women's Salon", "Beauty Parlour", "Bridal Studio", "Spa", "Home Service"],
  },
  pet_care: {
    price_unit: "per_visit",
    title_placeholder: "e.g., Furry Friends · Dog grooming",
    desc_placeholder: "Species covered, services, prices, home service option…",
    tags_placeholder: "dog, grooming, walking, boarding",
    subtype_label: "Service",
    subtypes: ["Grooming", "Walking", "Boarding / Daycare", "Training", "Pet Sitting", "Pet Taxi"],
  },
  veterinarian: {
    price_unit: "per_visit",
    title_placeholder: "e.g., Dr. Anand · Small Animal Vet",
    desc_placeholder: "Species treated, specialities, timings, emergency availability…",
    tags_placeholder: "vet, small animal, surgery, vaccination",
    subtype_label: "Speciality",
    subtypes: ["General", "Surgery", "Exotic", "Avian", "Emergency", "Homeopathic Vet"],
  },
  photographer: {
    price_unit: "fixed",
    title_placeholder: "e.g., ShutterStories · Wedding & Portrait",
    desc_placeholder: "Style, packages, portfolio link, travel radius…",
    tags_placeholder: "wedding, candid, drone, cinematic",
    subtype_label: "Speciality",
    subtypes: ["Wedding", "Pre-Wedding", "Portrait", "Newborn / Family", "Corporate / Event", "Product / Food", "Fashion", "Drone / Aerial"],
  },
  wedding_planner: {
    price_unit: "fixed",
    title_placeholder: "e.g., Regal Weddings · Destination specialists",
    desc_placeholder: "Package inclusions, past weddings, vendor network, budget range…",
    tags_placeholder: "destination, decor, catering, mehendi",
    subtype_label: "Focus",
    subtypes: ["Full Planning", "Decor Only", "Destination", "Intimate / Micro", "Multi-day Traditional"],
  },
  elder_care: {
    price_unit: "month",
    title_placeholder: "e.g., PortĂ© Home Nursing · 12hr / 24hr",
    desc_placeholder: "Services, staff experience, medical support, meals…",
    tags_placeholder: "elder care, home nursing, attendant, dementia",
    subtype_label: "Care level",
    subtypes: ["Companion Care", "Home Attendant", "Medical Home Nursing", "Post-op Recovery", "Dementia / Alzheimer", "Palliative", "Assisted Living Facility"],
  },
  physiotherapy: {
    price_unit: "per_visit",
    title_placeholder: "e.g., ReviveRx Physio · Sports & Ortho",
    desc_placeholder: "Conditions treated, techniques, home visits, session length…",
    tags_placeholder: "physio, sports, ortho, home visit",
    subtype_label: "Speciality",
    subtypes: ["Sports Injury", "Orthopaedic", "Neuro", "Cardio-Pulmonary", "Paediatric", "Geriatric", "Home Visit"],
  },
  loan: {
    price_unit: "fixed",
    title_placeholder: "e.g., SBI Gold Loan · Up to ₹50 L @ 8.75% p.a.",
    desc_placeholder: "Loan type, max amount, tenure, interest rate, eligibility, documents required…",
    tags_placeholder: "gold loan, personal, mortgage, 8.75%",
    subtype_label: "Loan type",
    subtypes: ["Gold Loan", "Personal Loan", "Home Loan", "Loan Against Property", "Business Loan", "Vehicle Loan", "Education Loan", "Loan Against Securities", "Loan Against Metal Pocket"],
    subtype_required: true,
  },
  dating: {
    price_unit: "fixed",
    title_placeholder: "e.g., Ananya, 26, loves hiking & jazz",
    desc_placeholder: "About you, interests, what you're looking for. Photos are auto-verified by AI.",
    tags_placeholder: "hiking, jazz, foodie, delhi",
    subtype_label: "Looking for",
    subtypes: ["Long-term", "Marriage-minded", "Casual", "Friendship", "Networking"],
    subtype_required: true,
    kyc_required: true,
    min_age: 18,
  },
  vacation: {
    price_unit: "fixed",
    title_placeholder: "e.g., Bali 5N/6D Honeymoon · Flights + Villa",
    desc_placeholder: "Destination, duration, inclusions (flight/stay/meals/tours), highlights…",
    tags_placeholder: "bali, honeymoon, 5n6d, all-inclusive",
    subtype_label: "Vacation type",
    subtypes: ["Beach", "Mountain / Hills", "City Break", "Weekend Getaway", "Adventure / Trek", "Cruise", "Family", "Honeymoon", "Solo", "Pilgrimage", "Wildlife / Safari"],
    subtype_required: true,
  },
  investment: {
    price_unit: "fixed",
    title_placeholder: "e.g., SBI Tax-Saver FD · 7.1% p.a. · 5-yr lock-in",
    desc_placeholder: "Product, expected return, tenure, tax treatment, min investment, provider credentials…",
    tags_placeholder: "fd, tax-saver, 7.1%, sbi",
    subtype_label: "Investment type",
    subtypes: [
      "Gold (Physical)", "Gold (Digital / SGB)", "Silver", "Copper / Base metals", "Other Commodity",
      "Land & Building", "Real-estate REIT",
      "Life Insurance", "Health Insurance", "ULIP",
      "Fixed Deposit (FD)", "Recurring Deposit (RD)", "Corporate FD",
      "PPF / NSC / Tax Saving (80C)", "ELSS Mutual Fund", "NPS",
      "Equity — Stocks", "Equity — Mutual Fund", "Equity — SIP", "Bonds / Debentures", "Crypto",
    ],
    subtype_required: true,
  },
};

// Preset cities for typeahead. Coordinates lifted from public geocoding data.
export const PRESET_CITIES = [
  { name: "Mumbai", lat: 19.076, lng: 72.8777 },
  { name: "Delhi", lat: 28.6139, lng: 77.209 },
  { name: "Bengaluru", lat: 12.9716, lng: 77.5946 },
  { name: "Hyderabad", lat: 17.385, lng: 78.4867 },
  { name: "Chennai", lat: 13.0827, lng: 80.2707 },
  { name: "Kolkata", lat: 22.5726, lng: 88.3639 },
  { name: "Pune", lat: 18.5204, lng: 73.8567 },
  { name: "Ahmedabad", lat: 23.0225, lng: 72.5714 },
  { name: "Jaipur", lat: 26.9124, lng: 75.7873 },
  { name: "Surat", lat: 21.1702, lng: 72.8311 },
  { name: "Lucknow", lat: 26.8467, lng: 80.9462 },
  { name: "Kanpur", lat: 26.4499, lng: 80.3319 },
  { name: "Nagpur", lat: 21.1458, lng: 79.0882 },
  { name: "Indore", lat: 22.7196, lng: 75.8577 },
  { name: "Bhopal", lat: 23.2599, lng: 77.4126 },
  { name: "Patna", lat: 25.5941, lng: 85.1376 },
  { name: "Vadodara", lat: 22.3072, lng: 73.1812 },
  { name: "Ghaziabad", lat: 28.6692, lng: 77.4538 },
  { name: "Ludhiana", lat: 30.9010, lng: 75.8573 },
  { name: "Agra", lat: 27.1767, lng: 78.0081 },
  { name: "Nashik", lat: 19.9975, lng: 73.7898 },
  { name: "Varanasi", lat: 25.3176, lng: 82.9739 },
  { name: "Meerut", lat: 28.9845, lng: 77.7064 },
  { name: "Rajkot", lat: 22.3039, lng: 70.8022 },
  { name: "Amritsar", lat: 31.6340, lng: 74.8723 },
  { name: "Coimbatore", lat: 11.0168, lng: 76.9558 },
  { name: "Kochi", lat: 9.9312, lng: 76.2673 },
  { name: "Thiruvananthapuram", lat: 8.5241, lng: 76.9366 },
  { name: "Chandigarh", lat: 30.7333, lng: 76.7794 },
  { name: "Gurgaon", lat: 28.4595, lng: 77.0266 },
  { name: "Noida", lat: 28.5355, lng: 77.391 },
  { name: "Faridabad", lat: 28.4089, lng: 77.3178 },
  { name: "Guwahati", lat: 26.1445, lng: 91.7362 },
  { name: "Ranchi", lat: 23.3441, lng: 85.3096 },
  { name: "Bhubaneswar", lat: 20.2961, lng: 85.8245 },
  { name: "Dehradun", lat: 30.3165, lng: 78.0322 },
  { name: "Mysuru", lat: 12.2958, lng: 76.6394 },
  { name: "Vijayawada", lat: 16.5062, lng: 80.648 },
  { name: "Visakhapatnam", lat: 17.6868, lng: 83.2185 },
  { name: "Goa", lat: 15.2993, lng: 74.124 },
];

const _UNIT_LABEL = {
  hour: "hour", day: "day", night: "night", month: "month", km: "km",
  gram: "g", per_visit: "visit", per_person: "person",
  per_piece: "pc", per_pair: "pair",
};
export const priceLabel = (p, unit) => {
  if (!p) return "Contact";
  const n = Number(p).toLocaleString("en-IN");
  if (!unit || unit === "fixed") return `₹${n}`;
  return `₹${n} / ${_UNIT_LABEL[unit] || unit}`;
};
